import { useEffect, useState } from "react";
import { toast } from "sonner";
import client from "@/lib/glossyApi";
import { PhoneCall, ArrowRight, Check } from "lucide-react";

const dayLabel = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  return {
    wd: d.toLocaleDateString("en-GB", { weekday: "short" }),
    dm: d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
  };
};

export default function BookCall() {
  const [days, setDays] = useState([]);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", email: "", business_name: "", phone: "" });
  const [callType, setCallType] = useState("phone");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { client.get("/discovery-slots").then((r) => setDays(r.data.days || [])).catch(() => {}); }, []);
  const slots = (days.find((d) => d.date === date) || {}).slots || [];

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) { toast.error("Pick a day and a time first."); return; }
    setBusy(true);
    try {
      await client.post("/discovery-call", { ...form, date, time, call_type: callType });
      setDone({ date, time });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed — try another slot.");
      if (err?.response?.status === 409) {
        setTime("");
        client.get("/discovery-slots").then((r) => setDays(r.data.days || [])).catch(() => {});
      }
    }
    setBusy(false);
  };

  if (done) {
    const { wd, dm } = dayLabel(done.date);
    return (
      <div className="border border-gloss-gold bg-gloss-gold/10 p-8 sm:p-10 text-center" data-testid="bc-success">
        <Check size={28} className="text-gloss-gold mx-auto mb-4" />
        <p className="font-serif text-3xl tracking-tight">You're booked — {wd} {dm} at {done.time}.</p>
        <p className="font-sans text-base opacity-75 mt-3 max-w-md mx-auto">A confirmation is on its way to your inbox. We'll talk through exactly how the magazine would present your business — no obligation, no hard sell.</p>
        <p className="font-mono text-[11px] opacity-50 mt-4" data-testid="bc-spam-note">Can't see the confirmation within a minute? Check your junk/spam folder — and add hello@smemagazine.co.uk to your contacts.</p>
      </div>
    );
  }

  return (
    <div className="border border-cream/20 p-6 sm:p-10" data-testid="book-call-panel">
      <div className="flex items-center gap-2.5 mb-3">
        <PhoneCall size={16} className="text-gloss-gold" />
        <p className="overline text-gloss-gold">Prefer to talk it through?</p>
      </div>
      <h3 className="font-serif text-3xl sm:text-4xl tracking-tight leading-tight">Book a free discovery call.</h3>
      <p className="font-sans text-base opacity-70 mt-3 max-w-2xl leading-relaxed">
        Fifteen minutes with the editorial desk — we'll show you exactly how S/ME would present your business, answer anything, and you decide from there.
      </p>

      <p className="overline text-[10px] opacity-50 mt-7 mb-2">Pick a day</p>
      <div className="flex gap-2 overflow-x-auto pb-2" data-testid="bc-days">
        {days.map((d) => {
          const { wd, dm } = dayLabel(d.date);
          const full = d.slots.every((s) => s.taken);
          return (
            <button key={d.date} type="button" onClick={() => { if (!full) { setDate(d.date); setTime(""); } }} disabled={full}
              className={`shrink-0 px-4 py-3 border text-center transition-colors ${date === d.date ? "bg-gloss-gold text-ink border-gloss-gold" : "border-cream/25 hover:border-gloss-gold"} disabled:opacity-30`}
              data-testid={`bc-day-${d.date}`}>
              <span className="overline text-[10px] block">{wd}</span>
              <span className="font-serif text-lg leading-none">{dm}</span>
            </button>
          );
        })}
      </div>

      {date && (
        <>
          <p className="overline text-[10px] opacity-50 mt-5 mb-2">Pick a time — UK time</p>
          <div className="flex gap-2 flex-wrap" data-testid="bc-times">
            {slots.map((s) => (
              <button key={s.time} type="button" onClick={() => setTime(s.time)} disabled={s.taken}
                className={`px-5 py-2.5 border overline transition-colors ${time === s.time ? "bg-gloss-gold text-ink border-gloss-gold" : "border-cream/25 hover:border-gloss-gold"} disabled:opacity-30 disabled:line-through`}
                data-testid={`bc-time-${s.time}`}>
                {s.time}
              </button>
            ))}
          </div>
        </>
      )}

      {date && time && (
        <>
          <p className="overline text-[10px] opacity-50 mt-5 mb-2">How would you like to talk?</p>
          <div className="flex gap-2 flex-wrap" data-testid="bc-call-type">
            <button type="button" onClick={() => setCallType("phone")}
              className={`px-5 py-2.5 border overline transition-colors ${callType === "phone" ? "bg-gloss-gold text-ink border-gloss-gold" : "border-cream/25 hover:border-gloss-gold"}`}
              data-testid="bc-type-phone">Phone call</button>
            <button type="button" onClick={() => setCallType("video")}
              className={`px-5 py-2.5 border overline transition-colors ${callType === "video" ? "bg-gloss-gold text-ink border-gloss-gold" : "border-cream/25 hover:border-gloss-gold"}`}
              data-testid="bc-type-video">Video call</button>
          </div>
          {callType === "video" && <p className="font-mono text-[10px] opacity-50 mt-2">We'll email you a video link before the call — nothing to install.</p>}
        </>
      )}

      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-7">
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name"
          className="bg-transparent border border-cream/25 px-4 py-3 font-sans text-sm focus:outline-none focus:border-gloss-gold placeholder:opacity-50" data-testid="bc-name" />
        <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email"
          className="bg-transparent border border-cream/25 px-4 py-3 font-sans text-sm focus:outline-none focus:border-gloss-gold placeholder:opacity-50" data-testid="bc-email" />
        <input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Business name (optional)"
          className="bg-transparent border border-cream/25 px-4 py-3 font-sans text-sm focus:outline-none focus:border-gloss-gold placeholder:opacity-50" data-testid="bc-business" />
        <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)"
          className="bg-transparent border border-cream/25 px-4 py-3 font-sans text-sm focus:outline-none focus:border-gloss-gold placeholder:opacity-50" data-testid="bc-phone" />
        <button type="submit" disabled={busy || !date || !time}
          className="sm:col-span-2 bg-cream text-ink py-4 overline flex items-center justify-center gap-3 hover:bg-gloss-gold transition-colors disabled:opacity-40"
          data-testid="bc-submit">
          {busy ? "Booking…" : <>Book my call <ArrowRight size={15} /></>}
        </button>
      </form>
      <p className="font-mono text-[10px] opacity-45 mt-3">No payment, no commitment — just a conversation.</p>
    </div>
  );
}
